import { type PropsWithChildren, useState } from "react";
import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { Persona, updatePersonaValues } from "@/api/personas";
import { Textarea } from "../ui/textarea";

interface CustomRulesModalProps {
  personaId: string;
  personaData: Persona;
}

export function CustomRulesModal({
  children,
  personaId,
  personaData,
}: PropsWithChildren<CustomRulesModalProps>) {
  const [showModal, setShowModal] = useState(false);
  const [rules, setRules] = useState<string[]>(
    personaData.custom_prompt_rules || [],
  );
  const [isUpdating, setIsUpdating] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleAddRule = () => {
    if (rules.length >= 10) {
      toast({
        description: "Maximum of 10 rules allowed",
        variant: "destructive",
      });
      return;
    }
    setRules([...rules, ""]);
  };

  // Function to adjust textarea height
  const adjustTextareaHeight = (textarea: HTMLTextAreaElement) => {
    textarea.style.height = "auto"; // Reset height to recalculate
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  const handleUpdateRule = (index: number, value: string) => {
    const newRules = [...rules];
    newRules[index] = value;
    setRules(newRules);
  };

  const handleDeleteRule = (index: number) => {
    const newRules = rules.filter((_, i) => i !== index);
    setRules(newRules);
  };

  const handleSave = async () => {
    setIsUpdating(true);
    try {
      const filteredRules = rules.filter((rule) => rule.trim() !== "");
      await updatePersonaValues(personaId, {
        custom_prompt_rules: filteredRules,
      });
      queryClient.invalidateQueries({ queryKey: ["personas"] });
      toast({ description: "Custom rules updated successfully" });
      setShowModal(false);
    } catch {
      toast({
        description: "Error updating custom rules",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Dialog open={showModal} onOpenChange={setShowModal}>
      {children}
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Custom Agent Guidelines</DialogTitle>
          <DialogDescription className="space-y-2">
            <p className="text-sm text-muted-foreground mt-2">
              Custom Agent Guidelines allow you to explicitly strengthen or
              override specific behaviors of your agent. These guidelines
              influence each agent interaction, helping you:
            </p>
            <ul className="list-disc pl-6 text-sm text-muted-foreground">
              <li>
                Avoid repetitive phrases or topic references that your agent
                consistently references
              </li>
              <li>
                Customize response styling and formatting to shorten/lengthen
                responses
              </li>
              <li>Prevent unwanted behaviors or responses</li>
            </ul>
            <p className="text-sm text-muted-foreground mt-2">
              While these guidelines directly influence your agent's behavior,
              please note that due to the complex nature of the AI agent,
              enforcement may vary across different types of requests.
            </p>
            <p className="text-sm font-semibold text-muted-foreground mt-2">
              Caution - These guidelines are saved in your Agent personality and
              will influence all outputs
            </p>
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0 space-y-4 mt-4">
          {rules.map((rule, index) => (
            <div key={index} className="flex gap-2">
              <Textarea
                value={rule}
                onChange={(e) => {
                  handleUpdateRule(index, e.target.value);
                  adjustTextareaHeight(e.target);
                }}
                onFocus={(e) => adjustTextareaHeight(e.target)}
                ref={(textarea) => {
                  if (textarea) {
                    adjustTextareaHeight(textarea);
                  }
                }}
                className="flex-1 border min-h-[40px] text-sm  resize-none overflow-hidden"
                placeholder="Enter your custom rule here..."
              />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleDeleteRule(index)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          {rules.length < 10 && (
            <Button
              variant="outline"
              onClick={handleAddRule}
              className="w-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Rule
            </Button>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-4 flex-shrink-0">
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button onClick={handleSave} disabled={isUpdating}>
            Save Changes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
